import React from 'react'
import PropTypes from 'prop-types'
import { QUESTION_TYPES } from 'utils/constants'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  ACQ7View,
  AudioType,
  DateType,
  DateTimeType,
  Decimal,
  HorizontalSelectOne,
  ImageCapture,
  Integer,
  Likert,
  MultipleField,
  NumericRatingScale,
  ResumeReview,
  Review,
  ScribbleType,
  SelectMultiple,
  SelectOne,
  TextEntry,
  TextPrompt,
  TimeType,
  Timer,
  TimedMatchingGrid,
  VAScale,
  VAScale2021,
  VASHorizontal
} from './QuestionTypes'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion
} from '../../utils/instrumentUtils'

const renderQuestion = (question, props) => {
  const { type } = question

  switch (type) {
    case QUESTION_TYPES.acq7:
      return <ACQ7View {...props} />
    case QUESTION_TYPES.audio:
      return <AudioType {...props} />
    case QUESTION_TYPES.date:
      return <DateType {...props} />
    case QUESTION_TYPES.datetime:
      return <DateTimeType {...props} />
    case QUESTION_TYPES.decimal:
      return <Decimal {...props} />
    case QUESTION_TYPES.horizontalSelectOne:
      return <HorizontalSelectOne {...props} />
    case QUESTION_TYPES.imageCapture:
      return <ImageCapture {...props} />
    case QUESTION_TYPES.integer:
      return <Integer {...props} />
    case QUESTION_TYPES.likert:
      return <Likert {...props} />
    case QUESTION_TYPES.multipleField:
      return <MultipleField {...props} />
    case QUESTION_TYPES.numericRatingScale:
      return <NumericRatingScale {...props} />
    case QUESTION_TYPES.prompt:
      return <TextPrompt {...props} />
    case QUESTION_TYPES.resumeReview:
      return <ResumeReview {...props} />
    case QUESTION_TYPES.review:
      return <Review {...props} />
    case QUESTION_TYPES.scribble:
      return <ScribbleType {...props} />
    case QUESTION_TYPES.selectMultiple:
      return <SelectMultiple {...props} />
    case QUESTION_TYPES.selectOne:
      return <SelectOne {...props} />
    case QUESTION_TYPES.text:
      return <TextEntry {...props} />
    case QUESTION_TYPES.time:
      return <TimeType {...props} />
    case QUESTION_TYPES.timer:
      return <Timer {...props} />
    case QUESTION_TYPES.timedMatchingGrid:
      // GOLD-12178: TMG is authored as a full-page question, not siblings.
      // Keep the renderer so a mis-authored sibling_id still shows the
      // grid instead of a blank page.
      return <TimedMatchingGrid {...props} />
    case QUESTION_TYPES.vaScale:
      return <VAScale {...props} />
    case QUESTION_TYPES.vaScale2021:
      return <VAScale2021 {...props} />
    case QUESTION_TYPES.vasHorizontal:
      return <VASHorizontal {...props} />
    default:
      return null
  }
}

const onCompleteSiblingQuestion = ({
  question,
  id,
  completeQuestion,
  nextQ,
  webSurvey,
  submissionSurvey,
  nextQId,
  reviewFunctionalitySupported,
  isConsent = false,
  isLastQuestion = false,
  isSibling = false
}) => {
  if (!question) return
  const { answer } = question
  const submission = question
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ
  )
}

const Sibling = (props) => {
  const {
    allQuestions,
    checkLogicAndComplete,
    className,
    closeModal,
    completeQuestion,
    currentSurvey,
    deleteStagedFile,
    firstName,
    humiIsabellaDisabled,
    id,
    isClinicianRole,
    isConsent,
    isLastQuestion,
    nextQ,
    nextQId,
    openModal,
    order,
    params,
    participantId,
    renderBackButton,
    reviewFunctionalitySupported,
    scrollToPreviousQuestion,
    selectedLocale,
    setAllRequiredAnswered,
    siblingId,
    storedAnswer,
    storedInstrument,
    strings,
    studyId,
    submissionLoading,
    submissionSurvey,
    suppressAutoAdvance,
    surveyScroll,
    uploadV2File,
    webSurvey
  } = props
  const _className = className ? ` ${className}` : ''
  const lastSiblingQuestionId = order.slice(-1)[0]
  const lastSibling =
    submissionSurvey.questions.find((question) => question.id === lastSiblingQuestionId) ||
    submissionSurvey.questions.find((question) => question.id === id) ||
    { id: lastSiblingQuestionId, attributes: webSurvey?.questions?.[lastSiblingQuestionId]?.attributes || {} }

  const questionFooter = (
    <QuestionFooter
      id={id}
      isConsent={isConsent}
      isLastQuestion={isLastQuestion}
      loading={submissionLoading}
      onComplete={() =>
        onCompleteSiblingQuestion({
          ...props,
          question: lastSibling,
          id: lastSiblingQuestionId
        })
      }
      question={lastSibling || {}}
      siblingOrder={order}
      submissionSurvey={submissionSurvey}
      renderBackButton={renderBackButton}
      siblingId={siblingId}
      strings={strings}
      webSurvey={webSurvey}
    />
  )

  const sharedProps = {
    checkLogicAndComplete,
    className: order.length > 1 ? 'has-siblings' : '',
    closeModal,
    completeQuestion,
    currentSurvey,
    deleteStagedFile,
    firstName,
    humiIsabellaDisabled,
    isClinicianRole,
    isConsent,
    isSibling: true,
    nextQ,
    nextQId,
    openModal,
    params,
    participantId,
    renderBackButton,
    reviewFunctionalitySupported,
    scrollToPreviousQuestion,
    selectedLocale,
    setAllRequiredAnswered,
    siblingOrder: order,
    storedAnswer,
    storedInstrument,
    strings,
    studyId,
    submissionSurvey,
    suppressAutoAdvance,
    surveyScroll,
    uploadV2File,
    webSurvey
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div className={`survey-question sibling${_className}`}>
          {order.map((questionId, idx) => {
            const question = allQuestions[questionId]
            const questionProps = {
              ...sharedProps,
              id: questionId,
              key: questionId,
              question
            }
            return renderQuestion(question, questionProps)
          })}
          {questionFooter}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedSibling = React.forwardRef((props, ref) => (
  <Sibling {...props} forwardedRef={ref} />
))

export default ForwardedSibling

Sibling.propTypes = {
  allQuestions: PropTypes.objectOf(PropTypes.object),
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  id: PropTypes.string,
  nextQ: PropTypes.func,
  nextQId: PropTypes.func,
  order: PropTypes.array,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  siblingId: PropTypes.string,
  strings: PropTypes.objectOf(PropTypes.string),
  submissionSurvey: PropTypes.object,
  webSurvey: PropTypes.object
}
